import DOMPurify from 'dompurify';
import { applyIf, isPresent, pipe, withImmutableData } from 'utils/functional';

const transformStringToDocument = (htmlString: string): Document => {
  const parser = new DOMParser();
  return parser.parseFromString(htmlString, 'text/html');
};

export const getSanitizedDocumentFromString = (dirty: string | Node, cfg?: DOMPurify.Config): Document => {
  const sanitizedHTML = DOMPurify.sanitize(dirty, cfg);
  return transformStringToDocument(sanitizedHTML);
};

const insertedMarkupClasses = ['editor-insert', 'editor-insert-disabled'];
const deletedMarkupClasses = ['editor-delete', 'editor-delete-disabled'];
const insertAndDeleteMarkupClasses = [...insertedMarkupClasses, ...deletedMarkupClasses];
const classNameSelector = (classNames: string[]) => classNames.map((cn) => `.${cn}`).join(', ');
const tagNameSelector = (tagNames: string[]) => tagNames.map((tn) => tn.toLowerCase()).join(', ');

const cloneDocument = (doc: Document): Document => {
  return doc.cloneNode(true) as Document;
};

const withImmutableDocument = withImmutableData(cloneDocument);

const removerLogic =
  (targets: string[], buildSelectorFn = tagNameSelector, shouldRemove = (element: Element) => true) =>
  (doc: Document): Document => {
    const selector = buildSelectorFn(targets);
    const elementsToRemove = doc.querySelectorAll(selector);
    elementsToRemove.forEach((element) => {
      if (shouldRemove(element)) {
        element.remove();
      }
    });
    return doc;
  };

const removeNodesBy = (
  targets: string[],
  buildSelectorFn?: (targets: string[]) => string,
  shouldRemove = (_element: Element) => true
) => withImmutableDocument(removerLogic(targets, buildSelectorFn, shouldRemove));

export const removeInsertedAndDeletedNodes = removeNodesBy(insertAndDeleteMarkupClasses, classNameSelector);
export const removeDeletedNodes = removeNodesBy(deletedMarkupClasses, classNameSelector);

const shouldRemoveIfOlderThan =
  (comparisonDate?: string | null) =>
  (element: Element): boolean => {
    if (!comparisonDate) {
      return false;
    }
    const createdAtAttribute = element.getAttribute('data-created-at');
    if (!createdAtAttribute) {
      return false;
    }

    try {
      const elementDate = new Date(createdAtAttribute);
      const compareToDate = new Date(comparisonDate);
      return elementDate < compareToDate;
    } catch (error) {
      console.error('Invalid date format in data-created-at attribute:', createdAtAttribute, error);
      return false;
    }
  };

export const removeIssuedDeletedNodes = (doc: Document, issuedAt?: string | null) =>
  applyIf(
    isPresent(issuedAt),
    removeNodesBy(deletedMarkupClasses, classNameSelector, shouldRemoveIfOlderThan(issuedAt)),
    // Maintain immutability if issuedAt is not present
    cloneDocument
  )(doc);

const replacerLogic =
  (
    targets: string[],
    buildSelectorFn: (targets: string[]) => string,
    replacerFn: (element: Element) => Node | string,
    shouldReplace = (_element: Element) => true
  ) =>
  (doc: Document): Document => {
    const selector = buildSelectorFn(targets);
    const elementsToReplace = doc.querySelectorAll(selector);
    elementsToReplace.forEach((element) => {
      if (shouldReplace(element)) {
        const newNode = replacerFn(element);
        if (typeof newNode === 'string') {
          // If it's HTML string, parse it and replace with actual nodes
          if (newNode.includes('<') && newNode.includes('>')) {
            const tempDiv = doc.createElement('div');
            tempDiv.innerHTML = newNode;
            const nodes = Array.from(tempDiv.childNodes);
            if (nodes.length === 1) {
              element.replaceWith(nodes[0]);
            } else if (nodes.length > 1) {
              element.replaceWith(...nodes);
            } else {
              element.replaceWith(doc.createTextNode(newNode));
            }
          } else {
            // Plain text
            element.replaceWith(doc.createTextNode(newNode));
          }
        } else {
          element.replaceWith(newNode);
        }
      }
    });
    return doc;
  };

const replaceNodesBy = (
  targets: string[],
  buildSelectorFn: (targets: string[]) => string,
  replacerFn: (element: Element) => Node | string,
  shouldReplace?: (element: Element) => boolean
) => withImmutableDocument(replacerLogic(targets, buildSelectorFn, replacerFn, shouldReplace));

const unwrapReplacer = (element: Element): string => {
  // If the element has children with formatting classes, preserve them
  const children = Array.from(element.childNodes);

  // Check if we have child elements with PhxEditorTheme classes
  const hasFormattingChildren = children.some((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const childElement = child as Element;
      return childElement.className && childElement.className.includes('PhxEditorTheme__');
    }
    return false;
  });

  // If we have formatting children, preserve the innerHTML
  // Otherwise, just return the text content
  return hasFormattingChildren ? element.innerHTML || '' : element.textContent || '';
};
const unwrapNodesByClass = (classNames: string[], shouldReplace?: (element: Element) => boolean) =>
  replaceNodesBy(classNames, classNameSelector, unwrapReplacer, shouldReplace);

export const unWrapInsertedNodes = unwrapNodesByClass(insertedMarkupClasses);
export const unWrapIssuedInsertedNodes = (doc: Document, issuedAt?: string | null) =>
  applyIf(
    isPresent(issuedAt),
    unwrapNodesByClass(insertedMarkupClasses, shouldRemoveIfOlderThan(issuedAt)),
    cloneDocument
  )(doc);

export const syncDocumentContent = (originalDoc: Document, modifiedDoc: Document): void => {
  originalDoc.documentElement.replaceWith(modifiedDoc.documentElement);
};

/**
 * Cleans up track changes for storage based on issuedAt date.
 * - Removes editor-delete nodes older than issuedAt
 * - Unwraps editor-insert nodes older than issuedAt (content preserved)
 *
 * This should be called when saving contentEditable to ensure stored
 * content is always clean relative to the issue date.
 */
export const cleanupTrackChangesForStorage = (htmlString: string, issuedAt?: string | null): string => {
  if (!htmlString || !issuedAt) return htmlString;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const cleanedDoc = pipe(
    (d: Document) => removeIssuedDeletedNodes(d, issuedAt),
    (d: Document) => unWrapIssuedInsertedNodes(d, issuedAt)
  )(doc);

  return cleanedDoc.body.innerHTML.trim();
};
